import React from 'react';
import { Sparkle } from 'lucide-react';

// Small inline badge showing how many credits an action will consume.
// The 4-point Sparkle icon is the agreed brand mark for credits — keep
// it consistent with any credit display elsewhere in the app.

type Tone = 'default' | 'on-primary' | 'subtle';
type Size = 'sm' | 'md';

const SIZE_CLASSES: Record<Size, { wrapper: string; icon: string }> = {
  sm: { wrapper: 'px-1 py-0 text-[10px] gap-0.5 rounded', icon: 'w-2.5 h-2.5' },
  md: { wrapper: 'px-1.5 py-0.5 text-[11px] gap-1 rounded', icon: 'w-3 h-3' },
};

const TONE_CLASSES: Record<Tone, string> = {
  // For neutral/light surfaces — uses brand primary tint.
  default: 'bg-brand-primary/10 text-brand-primary',
  // For sitting on top of a saturated brand-primary button (white text).
  'on-primary': 'bg-white/20 text-white',
  // Quiet variant for inline body copy / disabled states.
  subtle: 'bg-black/[0.06] text-brand-black/70',
};

interface CreditBadgeProps {
  credits: number;
  size?: Size;
  tone?: Tone;
  className?: string;
  // Optional human-readable label like "Necesitás 5 créditos para generar".
  // Rendered as `title` so it surfaces on hover without taking layout space.
  title?: string;
}

export const CreditBadge: React.FC<CreditBadgeProps> = ({
  credits,
  size = 'md',
  tone = 'default',
  className = '',
  title,
}) => {
  const sz = SIZE_CLASSES[size];
  const tn = TONE_CLASSES[tone];
  return (
    <span
      title={title}
      className={`inline-flex items-center font-semibold tabular-nums ${sz.wrapper} ${tn} ${className}`}
    >
      <Sparkle className={sz.icon} />
      {credits}
    </span>
  );
};

export default CreditBadge;
